import {
  AuditOutlined,
  ContactsOutlined,
  FolderOutlined,
  FundProjectionScreenOutlined,
  HistoryOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  MoneyCollectOutlined,
  NotificationOutlined,
  PhoneOutlined,
  PieChartOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  UserOutlined
} from '@ant-design/icons'
import {
  leadsTypesListOptions,
  opportunitiesPipelinesListOptions,
  reportsListOptions,
  tasksListOptions
} from '@crm/api'
import { paths } from '@crm/routes'
import { UserRoleCRM } from '@crm/types/userRoleCrm'
import { defaultNonPagination } from '@crm/utils'
import { OPERATIONAL_ROLES } from '@crm/utils/permissions'
import { useSessionContext } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { useQuery } from '@tanstack/react-query'

import type { AnyObject } from 'antd/es/_util/type'

export interface Module {
  iconType?: string
  iconComponent?: React.FC
  label: string
  route?: string
  params?: AnyObject
  index?: boolean
  children?: Modules
  count?: number
}

interface Modules {
  [key: string]: Module
}

export const useModules = (enabled?: boolean) => {
  const { t } = useLocales()
  const { user } = useSessionContext()
  const role = user?.current_application?.application_role as UserRoleCRM
  const isAdmin = role === UserRoleCRM.admin
  const isOperationalRole = OPERATIONAL_ROLES.includes(role)

  const { data: reports } = useQuery({ ...reportsListOptions(), enabled })

  const { data: leadsTypes } = useQuery({
    ...leadsTypesListOptions({ query: defaultNonPagination }),
    enabled
  })

  const { data: tasks } = useQuery({
    ...tasksListOptions({
      query: { completed: false, owner: [String(user?.user?.sso_id)] }
    }),
    enabled
  })

  const { data: opportunityPipelines } = useQuery({
    ...opportunitiesPipelinesListOptions({ query: defaultNonPagination }),
    enabled
  })

  const hasOpportunitiesPipelines = !!opportunityPipelines?.results?.length
  const hasLeadsPipelines = !!leadsTypes?.results?.length

  // Lead generation is nested under Leads, so the submenu now also exists for
  // admins of an organization that has no lead-type pipelines configured.
  const hasLeadsChildren = hasLeadsPipelines || isAdmin

  const leadsChildren: Modules = {
    leads: {
      label: t('menu.leads_list'),
      route: '/leads',
      params: {}
    },

    ...(hasLeadsPipelines &&
      leadsTypes?.results?.reduce((previousObject, { id, name }) => {
        return Object.assign(previousObject, {
          [name]: {
            label: name,
            route: `/leads-list/${id}?lead_type=${id}`,
            params: {}
          }
        })
      }, {})),

    ...(isAdmin && {
      leadGeneration: {
        label: t('menu.lead_generation'),
        route: paths.leadGeneration.root,
        params: {}
      }
    })
  }

  // A submenu title only expands, so the parent drops its own route once the
  // children own '/leads' — repeating it would collide on the antd menu key.
  const leadsParentRoute = hasLeadsChildren ? undefined : '/leads'
  const leadsRoute = hasLeadsPipelines ? '/leads-list' : leadsParentRoute

  const modules: Modules = {
    dashboard: {
      iconComponent: HomeOutlined,
      label: t('menu.dashboard'),
      route: '/',
      params: {},
      index: true
    },

    leads: {
      iconComponent: UserOutlined,
      label: t('menu.leads'),
      route: leadsRoute,
      params: {},
      ...(hasLeadsChildren && { children: leadsChildren })
    },

    opportunities: {
      iconComponent: MoneyCollectOutlined,
      label: t('lead.opportunities'),
      route: hasOpportunitiesPipelines
        ? '/opportunities-list'
        : '/opportunities',
      params: {},

      ...(hasOpportunitiesPipelines && {
        children: {
          opportunities: {
            label: t('menu.opportunities_list'),
            route: '/opportunities',
            params: {}
          },
          ...opportunityPipelines?.results?.reduce(
            (previousObject, { id, name }) => {
              return Object.assign(previousObject, {
                [name]: {
                  label: name,
                  route: `/opportunities-list/${id}?pipeline=${id}`
                }
              })
            },
            {}
          )
        }
      })
    },

    customers: {
      iconComponent: TeamOutlined,
      label: t('menu.customers'),
      route: '/clients',
      params: {}
    },

    ...(isAdmin && {
      contacts: {
        iconComponent: ContactsOutlined,
        label: t('menu.contacts'),
        route: '/contacts',
        params: {}
      }
    }),

    ...(isOperationalRole && {
      tasks: {
        iconComponent: UnorderedListOutlined,
        label: t('menu.tasks'),
        route: '/tasks',
        params: { owner: user?.user?.sso_id },

        count: tasks?.count ? tasks?.count : undefined
      }
    }),

    ...(isOperationalRole && {
      logs: {
        iconComponent: HistoryOutlined,
        label: t('menu.logs'),
        route: paths.logs,
        params: {}
      }
    }),

    ...(isAdmin && {
      calls: {
        iconComponent: PhoneOutlined,
        label: t('menu.calls'),
        route: '/calls',
        params: {}
      }
    }),

    ...(isAdmin && {
      documents: {
        iconComponent: FolderOutlined,
        label: t('menu.documents'),
        route: '/documents',
        params: {}
      }
    }),

    ...(isAdmin && {
      reports: {
        iconComponent: FundProjectionScreenOutlined,
        label: t('menu.reports'),
        route: '/report',
        params: {},

        children: {
          dashboard: {
            iconType: 'agents',
            label: t('reports.report_dashboard'),
            route: '/report/dashboard',
            params: {}
          },
          expenses: {
            iconType: 'nomenclatures',
            label: t('reports.report_expenses'),
            route: '/report/expenses',
            params: {}
          },
          reportSalesFunnel: {
            iconType: 'report-sales-funnel',
            label: t('reports.report_sales_funnel'),
            route: '/report/sales-funnel',
            params: {}
          },
          reportProducts: {
            iconType: 'report-products',
            label: t('reports.products'),
            route: '/report/products',
            params: {}
          },
          ...(typeof window !== 'undefined' &&
          window.location.hostname.split('.')[0] === 'trendsetter'
            ? {
                trendsetterReports: {
                  iconType: 'report-products',
                  label:
                    t('reports.trendsetter_reports') || 'Trendsetter Reports',
                  route: '/report/trendsetter',
                  params: {}
                }
              }
            : {}),
          ...reports?.reduce((previousObject, { id, name }) => {
            return Object.assign(previousObject, {
              [name]: {
                label: name,
                route: `/report/generic/${id}`,
                params: {}
              }
            })
          }, {})
        }
      }
    }),

    ...(isAdmin && {
      admin: {
        iconComponent: PieChartOutlined,
        label: t('menu.admin'),
        params: {},
        children: {
          nomenclatures: {
            label: t('menu.nomenclatures'),
            route: paths.admin.nomenclatures.root,
            params: {}
          },
          reports: {
            label: t('menu.reports'),
            route: '/report-management',
            params: {}
          },
          agents: {
            label: t('menu.agents'),
            route: '/agents',
            params: {}
          },
          integrations: {
            label: t('account_settings.integrations'),
            route: paths.admin.integrations.root,
            params: {}
          },
          settings: {
            label: t('menu.settings'),
            route: '/settings',
            params: {}
          }
        }
      }
    }),

    ...(isAdmin && {
      billing: {
        iconComponent: AuditOutlined,
        label: t('menu.billing'),
        params: {},
        route: '/billing',
        children: {
          invoices: {
            label: t('menu.invoices'),
            route: '/billing/invoices',
            params: {}
          },
          reports: {
            label: t('menu.reports'),
            route: '/billing/reports',
            params: {}
          },
          settings: {
            label: t('menu.settings'),
            route: '/billing/company-settings',
            params: {}
          }
        }
      }
    }),

    ...(isAdmin && {
      campaigns: {
        iconComponent: NotificationOutlined,
        label: t('menu.campaigns'),
        route: '/campaigns',
        params: {}
      }
    }),

    ...(isAdmin && {
      information: {
        iconComponent: InfoCircleOutlined,
        label: t('menu.information'),
        route: '/information',
        params: {}
      }
    })
  }

  return {
    modules
  }
}
